import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Icon, IconButton, Tooltip } from '@mui/material';
import { V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import { FilterListOff } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Loader from '../../elements/Loader';
import EmployeeAvatar from '../../elements/EmployeeAvatar';
import { LoaderSize } from '../../constants/constants';
import { ApiService } from '../../hooks';
import useAuthContext from '../../hooks/useAuthContext';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { useStartOfWorkingDays } from '../../hooks/useStartOfWorkingDays';
import { computeExpectedEndTime, computeTotalDurationFromOccurrenceItems } from '../../utils/taskTimeHelper';
import { TaskPlanItemType } from '../../models/common';
import { LocationStable, LocationStableTranslations } from '../../models/locations';
import { getFilters, saveFilters } from '../../utils';
import { ReloadIconButton } from '../../elements';
import { getISOWeekNumber } from '../../utils/date-range';
import {
  HealthSystemDomainTaskLibraryResultUserTaskOccurrencesViewModel,
  HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem
} from '../../api/HealthSystemApi';

interface TaskOverviewFilters {
  date: string | null;
}

const OccurrenceItemRow: React.FC<{
  item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem;
  name: string;
}> = ({ item, name }) => {
  const { t } = useTranslation();
  const { formatTime } = useFarmDateTime();
  const [expanded, setExpanded] = React.useState(false);

  const isDone = item.completedAt !== null && item.completedAt !== undefined;
  const subtasks = item.type === TaskPlanItemType.Vaccination ? (item.fromVaccinationRule?.subtasks ?? []) : [];
  const completedSubtasks = subtasks.filter((sub) => sub.completed).length;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1.5">
        {isDone ? (
          <CheckCircleOutlineIcon style={{ fontSize: 16 }} className="!text-green-600 flex-shrink-0" />
        ) : (
          <RadioButtonUncheckedIcon style={{ fontSize: 16 }} className="!text-gray-400 flex-shrink-0" />
        )}
        <span className={`text-xs truncate flex-1 ${isDone ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
          {name}
        </span>
        {isDone && <span className="text-xs text-gray-500 tabular-nums">{formatTime(item.completedAt!)}</span>}
        {!isDone && subtasks.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? t('f-com-hide') : t('f-com-expand')}
            className="flex cursor-pointer items-center border-0 bg-transparent p-0 text-xs text-indigo-700 tabular-nums"
          >
            {completedSubtasks}/{subtasks.length}
            <Icon className="!text-base/none">{expanded ? 'expand_less' : 'expand_more'}</Icon>
          </button>
        )}
      </div>
      {expanded && (
        <div className="ml-5 mt-1 flex flex-col gap-0.5">
          {subtasks.map((sub) => {
            const partial = !sub.completed && sub.completedDose > 0;
            return (
              <div key={sub.id} className="flex items-center gap-1.5 text-xs">
                <Icon
                  className={`!text-sm/none ${
                    sub.completed ? 'text-green-600' : partial ? 'text-amber-600' : 'text-gray-400'
                  }`}
                >
                  {sub.completed ? 'check_circle_outline' : partial ? 'timelapse' : 'radio_button_unchecked'}
                </Icon>
                <span className="flex-1 truncate text-gray-700">{sub.number || sub.location || '—'}</span>
                <span className="text-gray-500 tabular-nums whitespace-nowrap">
                  {sub.completedDose.toFixed(2)} / {sub.dose?.toFixed(2) ?? '0'} CC
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TaskOverview: React.FC<any> = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const { formatTime } = useFarmDateTime();

  const apiService = new ApiService();
  const storageKey = `TASK-OVERVIEW-FARM-${state.farmId}`;

  const saved = getFilters<TaskOverviewFilters>(storageKey);

  const [selectedDate, setSelectedDateFrom] = React.useState<Date | null>(
    saved?.date ? new Date(saved.date) : new Date()
  );
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [searchResult, setSearchResult] = React.useState<
    HealthSystemDomainTaskLibraryResultUserTaskOccurrencesViewModel[]
  >([]);

  const handleDateChange = (value: unknown) => {
    const date = value as Date | null;
    setSelectedDateFrom(date);
    saveFilters<TaskOverviewFilters>(storageKey, { date: date?.toISOString() ?? null });
    date && fetchSearchResults(date);
  };

  const handleClearFilters = () => {
    const today = new Date();
    setSelectedDateFrom(today);
    saveFilters<TaskOverviewFilters>(storageKey, { date: today.toISOString() });
    fetchSearchResults(today);
  };

  const fetchSearchResults = (date: Date) => {
    setIsBusy(true);
    apiService.api.taskList
      .taskListGetDailyUserOccurrences({ Date: date?.toISOString().split('T')[0] })
      .then((result) => setSearchResult(result.data))
      .finally(() => setIsBusy(false));
  };

  React.useEffect(() => {
    if (!loaded) {
      setIsBusy(true);
      selectedDate && fetchSearchResults(selectedDate);
      setLoaded(true);
    }
  }, [loaded, selectedDate]);

  const sortedUsers = React.useMemo(() => {
    return [...searchResult].sort((a, b) => {
      const aHas = a.occurrences.length > 0 ? 0 : 1;
      const bHas = b.occurrences.length > 0 ? 0 : 1;
      if (aHas !== bHas) return aHas - bHas;
      return (a.employee?.name ?? '').localeCompare(b.employee?.name ?? '');
    });
  }, [searchResult]);

  const allOccurrences = React.useMemo(() => searchResult.flatMap((user) => user.occurrences), [searchResult]);
  const startOfWorkingDays = useStartOfWorkingDays(allOccurrences);

  const getTaskName = (item: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem) => {
    if (item.fromTaskLibrary?.name) return item.fromTaskLibrary.name;
    return t('f-tasks-labor');
  };

  const getStableLabel = (stable: number) => {
    const key = LocationStableTranslations[stable as LocationStable];
    return key ? t(key) : t('f-loc-stable-unk');
  };

  const getCompletedCount = (items: HealthSystemDALModelTaskLibraryTaskPlanTaskOccurrenceItem[]) =>
    items.filter((i) => i.completedAt !== null && i.completedAt !== undefined).length;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-56">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    id: 'dateSelect',
                    margin: 'normal',
                    size: 'small',
                    'aria-label': 'change date',
                    fullWidth: true
                  }
                }}
                label={t('f-com-date')}
                value={selectedDate}
                onChange={handleDateChange}
              />
            </LocalizationProvider>
          </div>

          {selectedDate && (
            <span className="text-sm font-medium text-gray-500">
              {t('f-com-week')} {getISOWeekNumber(selectedDate)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Tooltip arrow title={t('m-global-clear-filters')}>
            <IconButton onClick={handleClearFilters} size="small" sx={V4_ICON_BUTTON_SMALL}>
              <FilterListOff />
            </IconButton>
          </Tooltip>
          <ReloadIconButton action={() => setLoaded(false)} />
        </div>
      </div>

      {/* Content */}
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {!isBusy && sortedUsers.length === 0 && <div className="text-center text-gray-500 py-12">{t('f-no-data')}</div>}

      {!isBusy && sortedUsers.length > 0 && (
        <div className="overflow-x-auto">
          <div className="flex gap-4 items-stretch min-w-min pb-2">
            {sortedUsers.map((user) => {
              const hasOccurrences = user.occurrences.length > 0;
              const totalTasks = user.occurrences.reduce((sum, o) => sum + o.items.length, 0);
              const completedTasks = user.occurrences.reduce((sum, o) => sum + getCompletedCount(o.items), 0);

              return (
                <div
                  key={user.employee?.id ?? 'unknown'}
                  className={`w-60 flex-shrink-0 bg-white rounded-lg border flex flex-col ${
                    hasOccurrences ? 'border-gray-200 shadow-sm' : 'border-dashed border-gray-300 opacity-60'
                  }`}
                >
                  {/* User header */}
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                    <EmployeeAvatar name={user.employee?.name} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {user.employee?.name ?? t('f-tasks-unassigned')}
                      </div>
                      {hasOccurrences && (
                        <div className="text-xs text-gray-500">
                          {completedTasks}/{totalTasks} {t('f-tasks-occurrences')}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Occurrences */}
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    {!hasOccurrences && <div className="text-xs text-gray-400 text-center py-4">{t('f-no-data')}</div>}

                    {user.occurrences.map((occurrence) => {
                      const completed = getCompletedCount(occurrence.items);
                      const total = occurrence.items.length;
                      const allDone = total > 0 && completed === total;
                      const startOfWorkingDay = startOfWorkingDays[occurrence.taskScheduleId];
                      const expectedEndTime = computeExpectedEndTime(
                        startOfWorkingDay,
                        computeTotalDurationFromOccurrenceItems(occurrence.items)
                      );

                      return (
                        <div
                          key={occurrence.id}
                          className={`rounded-md border p-3 ${
                            allDone
                              ? 'bg-green-50 border-green-200'
                              : completed > 0
                                ? 'bg-amber-50 border-amber-200'
                                : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          {/* Occurrence header */}
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-700">
                              {getStableLabel(occurrence.stable)}
                            </span>
                            <span
                              className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                allDone
                                  ? 'bg-green-100 text-green-700'
                                  : completed > 0
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {completed}/{total}
                            </span>
                          </div>

                          {/* Expected and actual times */}
                          <div className="mb-2 flex flex-col gap-0.5 text-xs text-gray-500">
                            {startOfWorkingDay && expectedEndTime && (
                              <span className="flex items-center gap-1">
                                <Icon className="!text-sm/none">schedule</Icon>
                                {t('f-tasks-expected-end-time')}: {startOfWorkingDay} - {expectedEndTime}
                              </span>
                            )}
                            {occurrence.completedAt ? (
                              <span className="flex items-center gap-1 text-green-700">
                                <Icon className="!text-sm/none">check</Icon>
                                {t('f-task-workspace-completed', 'Completed')}: {formatTime(occurrence.completedAt)}
                              </span>
                            ) : (
                              occurrence.startedAt && (
                                <span className="flex items-center gap-1">
                                  <Icon className="!text-sm/none">play_arrow</Icon>
                                  {t('f-task-workspace-started', 'Started')}: {formatTime(occurrence.startedAt)}
                                </span>
                              )
                            )}
                          </div>

                          {/* Task items */}
                          <div className="flex flex-col gap-1">
                            {occurrence.items.map((item) => (
                              <OccurrenceItemRow key={item.id} item={item} name={getTaskName(item)} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskOverview;
